import { useContext, useMemo } from 'react';
import {
  StatsigProvider as StatsigProviderCore,
  useClientAsyncInit,
  type StatsigUser,
} from '@statsig/react-bindings';
import type { OverrideAdapter } from '@statsig/client-core';
import { useAuthUser } from './hooks';
import { UserContext } from './contexts';
import { LoadingSpinner } from './components';

// In test mode, enable all feature gates so E2E tests can access gated features.
// Individual tests that need to verify "gate off" behavior should mock useFeatureGate directly.
// All methods on OverrideAdapter are optional — only getGateOverride is needed.
// See: https://docs.statsig.com/client/javascript-sdk/overrides
const testOverrideAdapter: OverrideAdapter = {
  getGateOverride: (gate) => ({ ...gate, value: true }),
};

const StatsigProvider = ({ user, children }: { user: StatsigUser; children: React.ReactNode }) => {
  const isTestMode = import.meta.env.MODE === 'test';
  const { client, isLoading } = useClientAsyncInit(import.meta.env.VITE_STATSIG_SDK_KEY, user, {
    networkConfig: {
      preventAllNetworkTraffic: isTestMode,
    },
    environment: {
      tier:
        import.meta.env.MODE === 'production'
          ? 'production'
          : import.meta.env.MODE === 'staging'
          ? 'staging'
          : 'development',
    },
    ...(isTestMode && { overrideAdapter: testOverrideAdapter }),
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return <StatsigProviderCore client={client}>{children}</StatsigProviderCore>;
};

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  const { name, user: authUser, clientId } = useAuthUser();
  const { roles, isSuperUser } = useContext(UserContext);
  const user: StatsigUser | null = useMemo(
    () =>
      authUser && name && clientId
        ? {
            email: authUser.email,
            appVersion: import.meta.env.VITE_APP_GIT_SHA,
            userID: name,
            custom: {
              roles,
              superUser: isSuperUser,
              clientId,
            },
            customIDs: {
              clientId,
            },
          }
        : null,
    [authUser, name, clientId, roles, isSuperUser],
  );

  if (!user) {
    return <>{children}</>;
  }

  return <StatsigProvider user={user}>{children}</StatsigProvider>;
};

export default Wrapper;
